import {Button} from '@ui/buttons/button';
import {Link} from 'react-router';
import type {BlockInstance} from '../appearance-data';
import {EyebrowPill} from './eyebrow-pill';
import {Reveal} from '../motion';
import {accent} from './accents';
import {Section, SectionHeading} from './section-kit';

type P = {block: BlockInstance; props: Record<string, any>};
const arr = (v: any): any[] => (Array.isArray(v) ? v : []);
const colsClass = (c: string) =>
  c === '6' ? 'md:grid-cols-6' : c === '5' ? 'md:grid-cols-5' : c === '4' ? 'md:grid-cols-4' : c === '2' ? 'md:grid-cols-2' : 'md:grid-cols-3';

export function ProgramCategoriesBlock({props}: P) {
  const items = arr(props.items);
  return (
    <Section index={props.__index} bgStyle={props.bg_style} bgColor={props.bg_color}>
      <div className="mx-auto max-w-[1100px]">
        <SectionHeading eyebrow={props.eyebrow} title={props.heading} subtitle={props.subheading} className="mb-36" />
        <div className={`grid grid-cols-2 gap-16 ${colsClass(props.columns)}`}>
          {items.map((c, i) => {
            // Rotate the tile colour per card — six identical primary chips is
            // what makes this grid read as flat.
            const a = accent(i);
            const inner = (
              <div className="ap-lift ap-shine flex h-full flex-col items-center rounded-2xl border border-divider bg-paper p-20 text-center shadow-sm transition hover:border-primary hover:shadow-lg">
                {c.image ? (
                  <img src={c.image} alt="" className="mb-12 h-56 w-56 rounded-2xl object-contain" />
                ) : c.icon ? (
                  <span className="mb-12 flex h-56 w-56 items-center justify-center rounded-2xl"
                    style={{backgroundColor: a.bg, color: a.fg}}>
                    <span className="material-icons text-3xl">{c.icon}</span>
                  </span>
                ) : null}
                {c.name ? <p className="text-sm font-semibold">{c.name}</p> : null}
                {c.meta ? <p className="mt-2 text-xs text-muted">{c.meta}</p> : null}
              </div>
            );
            return (
              <Reveal key={i} index={i}>
                {c.link ? <Link to={c.link}>{inner}</Link> : <div>{inner}</div>}
              </Reveal>
            );
          })}
        </div>
      </div>
    </Section>
  );
}

export function StudentJourneyBlock({props}: P) {
  const stages = arr(props.stages);
  return (
    <Section index={props.__index} bgStyle={props.bg_style || 'soft'} bgColor={props.bg_color}>
      <div className="mx-auto max-w-[1100px] text-center">
        <SectionHeading eyebrow={props.eyebrow} title={props.heading} subtitle={props.subheading} className="mb-36" />
        <div className="grid grid-cols-1 gap-0 md:grid-cols-4">
          {stages.map((s, i) => (
            <Reveal key={i} index={i} className="relative px-12 py-8">
              {i < stages.length - 1 && (
                // Gradient connector fades out toward the next step instead of a
                // flat hairline, so the row reads as a progression.
                <span className="absolute end-0 top-28 hidden h-2 w-full rounded-full bg-gradient-to-r from-primary/40 to-primary/5 md:block" aria-hidden />
              )}
              {/* Numbered stepper: the icon fills the circle and the step number
                  sits as a badge on the circle's edge (top corner). Falls back to
                  just the number when no icon is set. */}
              {/* Outer wrapper is NOT clipped so the number badge can sit on the
                  circle's edge; the inner circle clips the image to a round shape. */}
              <div className="relative z-10 mx-auto mb-12 h-56 w-56">
                <div className="flex h-full w-full items-center justify-center overflow-hidden rounded-full bg-primary text-white shadow-md">
                  {s.image ? (
                    <img src={s.image} alt="" className="h-full w-full object-cover" />
                  ) : s.icon ? (
                    <span className="material-icons text-2xl">{s.icon}</span>
                  ) : (
                    <span className="text-lg font-extrabold">{i + 1}</span>
                  )}
                </div>
                {(s.icon || s.image) && (
                  <span className="absolute -end-2 -top-2 z-20 flex h-22 w-22 items-center justify-center rounded-full border-2 border-paper bg-white text-2xs font-extrabold text-primary shadow-md">
                    {i + 1}
                  </span>
                )}
              </div>
              {s.title ? <h3 className="font-semibold">{s.title}</h3> : null}
              {s.text ? <p className="text-sm text-muted">{s.text}</p> : null}
            </Reveal>
          ))}
        </div>
      </div>
    </Section>
  );
}

/** Initials fallback when a team member has no photo. */
function initialsOf(name?: string): string {
  return String(name || '?')
    .trim()
    .split(/\s+/)
    .map(w => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

/** Contact / social icon row. Each link stops propagation so it opens itself
 *  rather than the card's profile link. */
function TeamContacts({p, onDark = false}: {p: any; onDark?: boolean}) {
  const links: {href: string; icon: string; label: string}[] = [];
  if (p.email) links.push({href: `mailto:${p.email}`, icon: 'mail', label: 'Email'});
  if (p.whatsapp) {
    links.push({
      href: `https://wa.me/${String(p.whatsapp).replace(/[^\d]/g, '')}`,
      icon: 'chat',
      label: 'WhatsApp',
    });
  }
  if (p.linkedin) links.push({href: p.linkedin, icon: 'link', label: 'LinkedIn'});
  if (!links.length) return null;

  return (
    <div className="mt-12 flex items-center justify-center gap-8">
      {links.map(l => (
        <a
          key={l.label}
          href={l.href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={l.label}
          onClick={e => e.stopPropagation()}
          className={`flex h-32 w-32 items-center justify-center rounded-full transition ${
            onDark
              ? 'bg-white/15 text-white hover:bg-white/30'
              : 'bg-primary/10 text-primary hover:bg-primary hover:text-white'
          }`}
        >
          <span className="material-icons text-base">{l.icon}</span>
        </a>
      ))}
    </div>
  );
}

/**
 * Team / advisors block. Two styles:
 *  - `portrait`: a large photo with a gradient scrim; name + role sit ON the
 *    photo, bio and contacts below. Reads like a real team page.
 *  - `avatar`: a compact round-avatar card (the old look, tidied up).
 */
export function ConsultantsBlock({props}: P) {
  const people = arr(props.people);
  if (!people.length) return null;
  const portrait = (props.card_style ?? 'portrait') !== 'avatar';
  const showContact = props.show_contact !== false;

  return (
    <Section index={props.__index} bgStyle={props.bg_style} bgColor={props.bg_color}>
      <div className="mx-auto max-w-[1100px]">
        <SectionHeading eyebrow={props.eyebrow} title={props.heading} subtitle={props.subheading} className="mb-36" />
        <div className={`grid grid-cols-1 gap-24 sm:grid-cols-2 ${colsClass(props.columns)}`}>
          {people.map((p, i) => {
            const inner = portrait ? (
              <div className="ap-lift ap-zoom group/team h-full overflow-hidden rounded-2xl border border-divider bg-paper shadow-sm transition hover:border-primary hover:shadow-xl">
                <div className="relative aspect-[4/5] overflow-hidden bg-primary/5">
                  {p.photo ? (
                    <img src={p.photo} alt={p.name || ''} className="h-full w-full object-cover" loading="lazy" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-primary/20 to-[#7c3aed]/20">
                      <span className="text-4xl font-extrabold text-primary/70">{initialsOf(p.name)}</span>
                    </div>
                  )}
                  {/* Scrim so the name stays readable over any photo. */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  {p.specialty ? (
                    <span className="absolute start-10 top-10 rounded-full bg-white/90 px-10 py-3 text-2xs font-semibold text-slate-800 shadow backdrop-blur">
                      {p.specialty}
                    </span>
                  ) : null}
                  <div className="absolute inset-x-0 bottom-0 p-14 text-start">
                    {p.name ? <h3 className="text-base font-bold leading-tight text-white drop-shadow">{p.name}</h3> : null}
                    {p.title ? <p className="mt-2 text-xs font-medium text-white/85">{p.title}</p> : null}
                  </div>
                </div>
                {(p.bio || showContact) && (
                  <div className="p-16 text-center">
                    {p.bio ? <p className="text-xs leading-relaxed text-muted">{p.bio}</p> : null}
                    {showContact ? <TeamContacts p={p} /> : null}
                  </div>
                )}
              </div>
            ) : (
              <div className="ap-lift h-full rounded-2xl border border-divider bg-paper p-24 text-center shadow-sm transition hover:border-primary hover:shadow-lg">
                {/* Gradient ring around the avatar — the small flourish that
                    makes an avatar grid look designed rather than default. */}
                <span className="mx-auto mb-14 block h-96 w-96 rounded-full bg-gradient-to-tr from-primary to-[#7c3aed] p-3">
                  <span className="block h-full w-full overflow-hidden rounded-full bg-paper p-2">
                    {p.photo ? (
                      <img src={p.photo} alt={p.name || ''} className="h-full w-full rounded-full object-cover" loading="lazy" />
                    ) : (
                      <span className="flex h-full w-full items-center justify-center rounded-full bg-primary/10 text-xl font-extrabold text-primary">
                        {initialsOf(p.name)}
                      </span>
                    )}
                  </span>
                </span>
                {p.name ? <h3 className="font-semibold">{p.name}</h3> : null}
                {p.title ? <p className="mt-2 text-sm font-medium text-primary">{p.title}</p> : null}
                {p.specialty ? (
                  <span className="mt-8 inline-block rounded-full bg-primary/10 px-10 py-3 text-2xs font-semibold text-primary">
                    {p.specialty}
                  </span>
                ) : null}
                {p.bio ? <p className="mt-10 text-xs leading-relaxed text-muted">{p.bio}</p> : null}
                {showContact ? <TeamContacts p={p} /> : null}
              </div>
            );
            return (
              <Reveal key={i} index={i}>
                {p.link ? <Link to={p.link}>{inner}</Link> : <div>{inner}</div>}
              </Reveal>
            );
          })}
        </div>
      </div>
    </Section>
  );
}

export function AiPromoBlock({props}: P) {
  return (
    <section className="px-24 py-64" style={{backgroundColor: props.bg_color || '#0b1220', color: props.text_color || '#fff'}}>
      <div className="mx-auto grid max-w-[1100px] items-center gap-32 md:grid-cols-2">
        <div>
          <EyebrowPill text={props.eyebrow} />
          {props.heading ? <h2 className="mb-12 text-2xl font-bold md:text-4xl">{props.heading}</h2> : null}
          {props.text ? <p className="mb-20 opacity-90">{props.text}</p> : null}
          {props.button_label ? (
            <Button variant="flat" color="primary" size="lg" className="rounded-full"
              elementType={Link} to={props.button_url || '/'}>{props.button_label}</Button>
          ) : null}
        </div>
        {props.image ? <img src={props.image} alt="" className="w-full rounded-2xl object-cover" /> : (
          <div className="flex justify-center"><span className="material-icons text-9xl opacity-30">smart_toy</span></div>
        )}
      </div>
    </section>
  );
}

export function SuccessStoriesBlock({props}: P) {
  const stories = arr(props.stories);
  return (
    <section className="px-24 py-56 bg-alt">
      <div className="mx-auto max-w-[1100px]">
        <SectionHeading eyebrow={props.eyebrow} title={props.heading} subtitle={props.subheading} className="mb-36" />
        <div className="grid grid-cols-1 gap-24 md:grid-cols-3">
          {stories.map((s, i) => (
            <div key={i} className="rounded-2xl border border-divider bg-paper p-24">
              <div className="mb-12 flex items-center gap-12">
                {s.photo ? <img src={s.photo} alt="" className="h-48 w-48 rounded-full object-cover" /> : null}
                <div>
                  {s.name ? <p className="font-semibold">{s.name}</p> : null}
                  {s.outcome ? <p className="text-xs font-medium text-positive">{s.outcome}</p> : null}
                </div>
              </div>
              {s.story ? <p className="text-sm text-muted">{s.story}</p> : null}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function BlogArticlesBlock({props}: P) {
  const articles = arr(props.articles);
  return (
    <Section index={props.__index} bgStyle={props.bg_style} bgColor={props.bg_color}>
      <div className="mx-auto max-w-[1100px]">
        <SectionHeading eyebrow={props.eyebrow} title={props.heading} subtitle={props.subheading} className="mb-36" />
        <div className="grid grid-cols-1 gap-24 md:grid-cols-3">
          {articles.map((a, i) => {
            const inner = (
              <div className="h-full overflow-hidden rounded-2xl border border-divider bg-paper transition hover:border-primary">
                {a.image ? <img src={a.image} alt={a.title || ''} className="aspect-video w-full object-cover" /> : null}
                <div className="p-16">
                  {a.date ? <p className="mb-4 text-2xs uppercase text-muted">{a.date}</p> : null}
                  {a.title ? <h3 className="mb-6 font-semibold">{a.title}</h3> : null}
                  {a.excerpt ? <p className="text-sm text-muted">{a.excerpt}</p> : null}
                </div>
              </div>
            );
            return a.link ? <Link key={i} to={a.link}>{inner}</Link> : <div key={i}>{inner}</div>;
          })}
        </div>
      </div>
    </Section>
  );
}
